import React, { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CompetitiveBenchmark } from '@/components/benchmark/CompetitiveBenchmark';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSite } from '@/contexts/SiteContext';
import { authFetch, handleResponse } from '@/utils/auth-fetch';
import { Sparkles, Trophy, Search, Package, Loader2, ArrowRight } from 'lucide-react';

interface Product {
  id: string;
  /** The feed SKU. Names are not unique, so this is the only field that identifies the row. */
  sku?: string | null;
  name: string;
  category: string;
  price?: number;
  imageUrl?: string;
}

/**
 * Competitive AEO Benchmark - Test Page
 *
 * Tests the complete AEO benchmark workflow:
 * 1. Generate AEO-optimized page
 * 2. Fetch competitor pages
 * 3. Analyze with AI judge
 * 4. Display competitive comparison
 */
export default function CompetitiveBenchmarkTest() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedSite } = useSite();

  // Read URL parameters - no defaults
  const sourceType = (searchParams.get('sourceType') as 'product' | 'category') || 'product';
  const sourceIdFromUrl = searchParams.get('sourceId') || '';
  const sourceNameFromUrl = searchParams.get('sourceName') || '';

  // State for product selection
  const [selectedProduct, setSelectedProduct] = useState<{ id: string; name: string } | null>(
    sourceIdFromUrl ? { id: sourceIdFromUrl, name: sourceNameFromUrl } : null
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searching, setSearching] = useState(false);
  const [recentProducts, setRecentProducts] = useState<Product[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  const benchmarkRef = useRef<{ addCompetitorUrl: (url: string) => void }>(null);

  // Load recent products when no product is selected
  useEffect(() => {
    if (!selectedProduct && selectedSite) {
      loadRecentProducts();
    }
  }, [selectedSite, selectedProduct]);

  const loadRecentProducts = async () => {
    if (!selectedSite) return;
    setLoadingRecent(true);
    try {
      const response = await authFetch(
        `/api/product-enrichment/recent?siteId=${selectedSite.id}&limit=6`
      );
      const data = await handleResponse<{ products: Product[] }>(response);
      setRecentProducts(data.products || []);
    } catch (err) {
      console.error('Failed to load recent products:', err);
    } finally {
      setLoadingRecent(false);
    }
  };

  const searchProducts = async () => {
    if (!selectedSite || !searchQuery.trim()) return;
    setSearching(true);
    try {
      const response = await authFetch(
        `/api/product-enrichment/search?siteId=${selectedSite.id}&q=${encodeURIComponent(searchQuery)}&limit=10`
      );
      const data = await handleResponse<{ products: Product[] }>(response);
      setSearchResults(data.products || []);
    } catch (err) {
      console.error('Failed to search products:', err);
    } finally {
      setSearching(false);
    }
  };

  const selectProduct = (product: Product) => {
    setSelectedProduct({ id: product.id, name: product.name });
    // Update URL for bookmarking
    setSearchParams({
      sourceType: 'product',
      sourceId: product.id,
      sourceName: product.name,
    });
  };

  const clearSelection = () => {
    setSelectedProduct(null);
    setSearchParams({});
  };

  // Show product selector if no product selected
  if (!selectedProduct) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="h-8 w-8 text-yellow-500" />
            <h1 className="text-3xl font-bold">Competitive AEO Benchmark</h1>
            <Badge variant="outline" className="ml-2">
              Beta
            </Badge>
          </div>
          <p className="text-lg text-muted-foreground">
            Compare your AEO-optimized pages against competitors and SERP leaders
          </p>
        </div>

        {/* Product Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-500" />
              Select a Product to Benchmark
            </CardTitle>
            <CardDescription>
              Choose a product from your catalog to compare against competitors
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Search Input */}
            <div className="flex gap-2">
              <Input
                placeholder="Search products by name or SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchProducts()}
              />
              <Button onClick={searchProducts} disabled={searching || !searchQuery.trim()}>
                {searching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Search Results</h4>
                <div className="grid gap-2">
                  {searchResults.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => selectProduct(product)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{product.name}</p>
                        {/* ⛔ THE SKU DISAMBIGUATES THE ROW (task-epvtogyjl8). /search now returns and
                            matches it; without showing it here, duplicate names stay visually
                            identical and the operator picks one at random. Measured on p4: 2,087
                            duplicated names cover 6,111 of 14,790 products, one name shared by 22. */}
                        {product.sku && (
                          <p className="text-xs font-mono text-muted-foreground select-text break-all">SKU {product.sku}</p>
                        )}
                        <p className="text-sm text-muted-foreground truncate">{product.category}</p>
                      </div>
                      <Button size="sm" variant="ghost">
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Products */}
            {recentProducts.length > 0 && searchResults.length === 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Recent Products</h4>
                {loadingRecent ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="grid gap-2 md:grid-cols-2">
                    {recentProducts.map((product) => (
                      <div
                        key={product.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                        onClick={() => selectProduct(product)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-sm">{product.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{product.category}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {recentProducts.length === 0 && searchResults.length === 0 && !loadingRecent && (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No products found. Try searching or add products to your catalog first.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-500" />
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Select a product from your catalog above</li>
              <li>We generate an AEO-optimized page for your product using AI</li>
              <li>You add competitor URLs (or we fetch the top SERP result automatically)</li>
              <li>Our AI analyzes all pages for AEO, SEO, and UX quality</li>
              <li>You get detailed rankings, scores, and improvement recommendations</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="h-8 w-8 text-yellow-500" />
          <h1 className="text-3xl font-bold">Competitive AEO Benchmark</h1>
          <Badge variant="outline" className="ml-2">
            Beta
          </Badge>
        </div>
        <p className="text-lg text-muted-foreground">
          Compare your AEO-optimized pages against competitors and SERP leaders
        </p>
      </div>

      {/* Selected Product Banner */}
      <Card className="mb-6 bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 text-green-600 dark:text-green-400" />
              <div>
                <p className="font-medium">{selectedProduct.name}</p>
                <p className="text-sm text-muted-foreground">Selected for benchmark</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={clearSelection}>
              Change Product
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="mb-6 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-500" />
            How It Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>We generate an AEO-optimized page for your product using AI</li>
            <li>You add competitor URLs (or we fetch the top SERP result automatically)</li>
            <li>Our AI analyzes all pages for AEO, SEO, and UX quality</li>
            <li>You get detailed rankings, scores, and improvement recommendations</li>
          </ol>
          <div className="mt-4 flex gap-2 flex-wrap">
            <Badge variant="secondary">GPT-4o-mini powered</Badge>
            <Badge variant="secondary">SERP integration</Badge>
            <Badge variant="secondary">30-60 second analysis</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Main Benchmark Component */}
      <CompetitiveBenchmark
        ref={benchmarkRef}
        sourceType={sourceType}
        sourceId={selectedProduct.id}
        sourceName={selectedProduct.name}
      />
    </div>
  );
}
